import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { buildHelpDocsUrl } from "src/common/util/helpDocsLighthouse/buildHelpDocsUrl";
import type { HelpArticle } from "./useHelpArticle";
import "./helpArticleBody.scss";

interface HelpArticlePanelProps {
  title: string;
  article: HelpArticle | null;
  open: boolean;
  onClose: () => void;
}

const HelpArticlePanel = ({ title, article, open, onClose }: HelpArticlePanelProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !article) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-[500] w-96 bg-white border-l border-gray-200 shadow-lg flex flex-col">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <p className="text-sm font-semibold text-gray-700">{title}</p>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
          aria-label={t("general.close")}
        >
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div
          className="help-article-body text-sm text-gray-700"
          dangerouslySetInnerHTML={{ __html: article.body }}
        />
        <a
          className="font-bold text-green-500 underline underline-offset-4 inline-block mt-4 text-sm"
          target="_blank"
          rel="noopener noreferrer"
          href={buildHelpDocsUrl(article.url)}
        >
          {t("pages.project.editNode.helpSectionOpenInHelpCenter")}
        </a>
      </div>
    </div>
  );
};

export default HelpArticlePanel;
